import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, Users, Clock, TrendingUp } from "lucide-react";

const HeroSection = () => {
  const stats = [
    { number: "3–5", label: "intake meetings per month", icon: Users },
    { number: "15+", label: "hours saved weekly", icon: Clock },
    { number: "£47K+", label: "value delivered", icon: TrendingUp },
  ];

  return (
    <main className="relative h-[100dvh] overflow-hidden flex flex-col">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background/80 to-secondary/20" />
      
      {/* Subtle grid pattern for grounding */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />
      
      {/* Radial glow behind content */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full opacity-30"
        style={{
          background: 'radial-gradient(ellipse at center, hsl(var(--primary) / 0.15) 0%, transparent 70%)'
        }}
      />
      
      {/* Floating orbs for visual effect */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-primary/20 rounded-full blur-3xl animate-float hidden md:block" />
      <div className="absolute bottom-40 right-20 w-48 h-48 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />

      {/* Main content - vertically centered */}
      <div className="flex-1 flex flex-col items-center justify-center pt-16 md:pt-[88px]">
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            {/* Eyebrow Text */}
            <div className="animate-fade-in px-2" style={{ marginBottom: '8px' }}>
              <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground uppercase tracking-widest font-medium mb-2 md:mb-4 whitespace-nowrap">
                For Recruitment Agencies Looking To Scale
              </p>
              <div 
                className="w-24 md:w-32 h-px mx-auto"
                style={{
                  background: "linear-gradient(90deg, transparent 0%, hsl(var(--primary) / 0.05) 20%, hsl(var(--primary) / 0.35) 50%, hsl(var(--primary) / 0.05) 80%, transparent 100%)",
                  boxShadow: "0 0 8px hsl(var(--primary) / 0.15)"
                }}
              />
            </div>

            {/* Main Headline */}
            <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.15] tracking-tight font-figtree animate-fade-in" style={{ animationDelay: '0.1s', marginBottom: '24px' }}>
              How To Book <span className="text-gradient">3–5</span> New<br className="hidden lg:block" /> Intake <span className="text-gradient">Meetings</span> Every <span className="text-gradient">Month</span>
            </h1>

            {/* Video Sales Letter */}
            <div className="w-full animate-fade-in" style={{ maxWidth: '680px', marginBottom: '24px', animationDelay: '0.15s' }}>
              <div className="h-[20vh] sm:h-auto sm:aspect-video bg-card/50 rounded-lg overflow-hidden">
                {/* @ts-ignore */}
                <wistia-player media-id="ety464gbb4" aspect="1.7777777777777777" style={{ width: '100%', height: '100%' }} />
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 md:gap-4 justify-center items-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <Button 
                variant="hero"
                size="lg" 
                className="hover-glow text-sm md:text-base px-6 py-4 md:px-8 md:py-6"
                onClick={() => {
                  requestAnimationFrame(() => {
                    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
                  });
                }}
              >
                <Calendar className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                Book Your Discovery Call
              </Button>
              
              <Button 
                variant="outline"
                size="lg" 
                className="border-2 border-primary/30 bg-card/50 hover:bg-card hover:border-primary/50 text-foreground text-sm md:text-base px-6 py-4 md:px-8 md:py-6"
                onClick={() => {
                  requestAnimationFrame(() => {
                    document.getElementById('who-is-this-for')?.scrollIntoView({ behavior: 'smooth' });
                  });
                }}
              >
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="relative z-10 border-t border-border/50 bg-card/30 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-3 divide-x divide-border/30">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div 
                  key={index} 
                  className="py-6 md:py-8 text-center animate-fade-in"
                  style={{ animationDelay: `${0.4 + index * 0.15}s` }}
                >
                  <Icon className="w-5 h-5 md:w-6 md:h-6 text-primary mx-auto mb-2 opacity-70" />
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-gradient mb-1">
                    {stat.number}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {stat.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </main>
  );
};

export default HeroSection;
